/**
 * Surname only, for the places too tight to carry a full provider name.
 *
 * The provider is inconsistent about the same player: one goal is filed as `A. Elanga`, the next as
 * `Anthony Elanga`, and `Evanilson` and `Francisco Evanilson` are one man. The compact fixture row
 * has room for the part that identifies him and nothing else, so both shapes reduce to the last
 * name — which has the side effect of making the two spellings agree on the page.
 *
 * A particle belongs to the name it precedes: `van Dijk` and `De Cuyper` are kept whole, however
 * many particles are stacked (`van der Sar`). A single-word name (`Rodri`, `Emersonn`) is left
 * exactly as it came.
 *
 * **The known cost.** A name that is not "given name, family name" is cut in the wrong place —
 * `Son Heung-min` reads as `Heung-min`. Nothing in a provider string says which convention it
 * follows, so this is a deliberate trade for a row that fits, not an oversight.
 */

/**
 * Name particles, matched case-insensitively — the provider capitalises `De Cuyper` and lowercases
 * `van Dijk`, and both are part of the surname.
 */
const PARTICLES = new Set([
  'van', 'von', 'de', 'del', 'della', 'der', 'den', 'di', 'da', 'das', 'dos',
  'du', 'la', 'le', 'el', 'al', 'bin', 'ibn', 'ter', 'ten', 'op', 'af', 'av',
])

export function surname(player: string): string {
  // Initials first, so `J. P. Ashcroft` is down to one token before the split below.
  const withoutInitials = player.replace(/^(?:\p{Lu}\p{M}*\.\s*)+/u, '')
  const parts = withoutInitials.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return player

  let start = parts.length - 1
  while (start > 0 && PARTICLES.has(parts[start - 1].toLowerCase())) start -= 1
  return parts.slice(start).join(' ')
}
